import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import H2 from '../../../components/Titles/h2';
import SystempayComponent from '../../../components/Paiement/systempay';
import Input from '../../../components/Inputs/input';
import Icon from '../../../components/Images/icon';
import iconPhone from "../../../assets/icons/contact/phone.svg";
import iconCalendar from "../../../assets/icons/quotation/calendar.svg";
import H4 from '../../../components/Titles/h4';
import _ from 'lodash';

export default function DetailPaiementQuotation({ selectedCard, quotation, group, quotations = [], returnedData }) {
    const { t } = useTranslation();
    const [description, setDescription] = useState(null);

    const isGroup = !!group;
    const items = useMemo(() => {
        if (isGroup && Array.isArray(quotations) && quotations.length) return quotations;
        return quotation ? [quotation] : [];
    }, [isGroup, quotations, quotation]);

    const groupStatus = _.get(group, 'status');

    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

    const computeLineAmount = useCallback((q, method, context) => {
        const { isGroup, groupStatus, singleQuotation } = context;

        const total = Number(_.get(q, 'prices.totalPrice', 0));
        const enable = !!_.get(q, 'prices.enableDeposit', false);
        const pct = Number(_.get(q, 'prices.depositPercentage', 0));

        // Eligible for a true deposit only if 0 < pct < 100 and enabled
        const depositEligible = enable && pct > 0 && pct < 100;
        const depositAmount = depositEligible ? round2(total * (pct / 100)) : round2(total);

        // --- Single quotation preview ---
        if (!isGroup) {
            if (method === 'payNow') return round2(total);
            if (method === 'payLater') return round2(depositAmount);
            if (method === 'payLeftToPay') {
                const st = _.get(singleQuotation, 'mainInfos.status');
                return st === 'depositPaid' ? round2(total - depositAmount) : 0;
            }
            return round2(total);
        }

        // --- Group preview ---
        const isAfterDeposit = ['depositPaid', 'paiement'].includes(groupStatus);

        if (method === 'payNow') return round2(total);
        if (method === 'payLater') return round2(depositAmount);

        if (method === 'payLeftToPay') {
            // Use the *actual* first-charge amount from the server snapshot (breakdown)
            if (!isAfterDeposit) return 0;
            const remainder = Math.max(0, round2(total - depositAmount));
            return remainder;
        }

        return round2(total);
    }, []);

    const amountToPay = useMemo(() => {
        const context = { isGroup, groupStatus, singleQuotation: quotation };
        const total = items.reduce((sum, q) => sum + computeLineAmount(q, selectedCard, context), 0);
        return round2(total);
    }, [items, selectedCard, isGroup, groupStatus, quotation, computeLineAmount]);

    const language = useMemo(() => {
        if (isGroup) return _.get(items, '[0].mainInfos.language', 'en');
        return _.get(quotation, 'mainInfos.language', 'en');
    }, [isGroup, items, quotation]);

    // Build payload for Systempay
    const sysData = useMemo(() => {
        if (isGroup) {
            return {
                type: 'group',
                paymentMethod: selectedCard,      // "payNow" | "payLater" | ...
                groupKey: group?.key,            // prefer key
                groupId: group?._id,             // fallback if needed server-side
                language
            };
        }
        return {
            type: 'quotation',
            paymentMethod: selectedCard,
            quotationID: quotation?._id,
            language
        };
    }, [isGroup, selectedCard, group, quotation, language]);

    useEffect(() => {
        if (selectedCard === 'payLater') {
            setDescription(isGroup
                ? (t('pageQuotation.step3.payLater.descriptionGroup') || t('pageQuotation.step3.payLater.shortDescription'))
                : (t('pageQuotation.step3.payLater.description1') + _.get(quotation, 'prices.depositPercentage', 0) + '%' + t('pageQuotation.step3.payLater.description2'))
            );

        } else if (selectedCard === 'payLeftToPay') {
            setDescription(t('pageQuotation.step3.payLeftToPay.description') || t('pageQuotation.step3.payNow.description'));

        } else if (selectedCard === 'payNow') {
            setDescription(isGroup
                ? (t('pageQuotation.step3.payNow.descriptionGroup'))
                : (t('pageQuotation.step3.payNow.description'))
            );

        } else {
            setDescription(t(`pageQuotation.step3.${selectedCard}.description`));
        }
    }, [t, selectedCard, isGroup, quotation]);

    const handleReturnedData = (data) => returnedData(data);

    return (
        <div className='px-4 md:px-0'>
            <div className='flex flex-col gap-5 md:flex-row'>
                <div className='w-full md:w-5/12'>
                    <H2 title={t(`pageQuotation.step3.${selectedCard}.title`)} />
                    <p className='text-description opacity-60 mt-3'>{description}</p>

                    {selectedCard === 'payByTransfer' && !isGroup && (
                        <p className='text-description opacity-60 mt-5'>
                            {t("pageQuotation.step3.payByTransfer.detailsName")}
                            <span className='uppercase'>{quotation.customer.lastname}</span>&nbsp;
                            <span>{quotation.customer.firstname}</span><br />
                            {t("pageQuotation.step3.payByTransfer.detailsNumberQuotation")}
                            {quotation.mainInfos.quotationNumber}
                        </p>
                    )}

                    {/* Show amount preview */}
                    {(selectedCard === 'payNow' || selectedCard === 'payLater') && (
                        <div className='mt-4'>
                            <H4
                                title={
                                    (t('pageQuotation.step3.amountToPay') || 'Montant à payer : ') +
                                    amountToPay.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
                                    ' €'
                                }
                                fontWeight='bold'
                            />
                        </div>
                    )}
                </div>

                <div className='w-full md:w-7/12 mb-8'>
                    {(selectedCard === 'payNow' || selectedCard === 'payLater' || selectedCard === 'payLeftToPay') && (
                        <SystempayComponent key={`${selectedCard}-${group?.key || quotation?._id || 'single'}`} data={sysData} returnedData={handleReturnedData} />
                    )}

                    {selectedCard === 'payByTransfer' && !isGroup && (
                        <div>
                            <div>
                                <Input label={t("bankCode.label")} value={t("bankCode.value")} />
                                <Input label={t("boxCode.label")} value={t("boxCode.value")} />
                                <Input label={t("accountNumber.label")} value={t("accountNumber.value")} />
                                <Input label={t("RIB.label")} value={t("RIB.value")} />
                                <Input label={t("IBAN.label")} value={t("IBAN.value")} />
                                <Input label={t("BIC.label")} value={t("BIC.value")} />
                            </div>
                            <div className='p-4'>
                                <H4
                                    title={(t('pageQuotation.step3.amountToPay') || '') +
                                        Number(_.get(quotation, 'prices.totalPrice', 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "€"}
                                    fontWeight='bold'
                                />
                            </div>
                        </div>
                    )}

                    {selectedCard === 'payByPhone' && !isGroup && (
                        <div>
                            <div className='flex flex-col gap-4 md:flex-row'>
                                <a href="tel:+33387880930" className="flex-1">
                                    <div className="p-4 xl:p-8 text-center w-fit m-auto flex flex-col items-center gap-4">
                                        <Icon icon={iconPhone} alt={t('contact.altIconPhone')} />
                                        <span className="font-bold text-md whitespace-nowrap">+ 33 (0)3 87 88 09 30</span>
                                        <span className="text-sm whitespace-nowrap -mt-2">{t('pageQuotation.step3.payByPhone.language')}</span>
                                    </div>
                                </a>

                                <a href="tel:+33387880930" className="flex-1">
                                    <div className="p-4 xl:p-8 text-center w-fit m-auto flex flex-col items-center gap-4">
                                        <Icon icon={iconCalendar} alt={t('pageQuotation.step3.payByPhone.calendar.altIcon')} />
                                        <span className="font-bold text-md whitespace-nowrap">
                                            {t('pageQuotation.step3.payByPhone.calendar.days')}
                                        </span>
                                        <span className="text-sm whitespace-nowrap -mt-2">
                                            {t('pageQuotation.step3.payByPhone.calendar.hours')}
                                        </span>
                                    </div>
                                </a>
                            </div>
                            <div className='p-4'>
                                <H4
                                    title={
                                        (t('pageQuotation.step3.amountToTransfer') || '') +
                                        Number(_.get(quotation, 'prices.totalPrice', 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "€"
                                    }
                                    fontWeight='bold'
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}